import {
  Autocomplete,
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderOptionState,
  Paper,
  Theme,
  useTheme,
  AutocompleteValue,
  FilterOptionsState,
} from '@nexelem/deprecated-theme';
import { CUSTOM_ICONS, IconComponentProps } from '@nexelem/icons';
import { CircularProgress, VirtualKeyboardPopper, VirtualKeyboardPopperEnable, Chip } from '@nexelem/components';

import {
  ComponentProps,
  FC,
  HTMLAttributes,
  JSX,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { InternalField, InternalFieldProps, StyledInternalGrid } from '../Internal';
import { DropdownIndicatorContainer } from './DropdownIndicatorContainer';
import { ClearButton } from '../Internal/ClearButton';
import { SelectFieldListbox } from './SelectFieldListbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';

type TagProps = {
  key: number;
  className: string;
  disabled: boolean;
  'data-tag-index': number;
  tabIndex: -1;
  onDelete: (event: SyntheticEvent) => void;
};
// https://www.w3schools.com/cssref/css_units.php
type UnitLengthCSS =
  | 'em'
  | 'ex'
  | '%'
  | 'px'
  | 'cm'
  | 'mm'
  | 'in'
  | 'pt'
  | 'pc'
  | 'ch'
  | 'rem'
  | 'vh'
  | 'vw'
  | 'vmin'
  | 'vmax';

export type SelectFieldNonFormProps = Pick<
  InternalFieldProps,
  | 'label'
  | 'placeholder'
  | 'id'
  | 'helperText'
  | 'errorMessage'
  | 'type'
  | 'size'
  | 'error'
  | 'disabled'
  | 'readOnly'
  | 'required'
  | 'showErrorBorder'
  | 'textAlign'
> & {
  readonly StartIcon?: FC<IconComponentProps>;
  readonly HelperTextIcon?: FC<IconComponentProps>;
  readonly isMultiple?: boolean;
  readonly width?: number | `${string}${UnitLengthCSS}`;
  readonly onScrollEnd?: () => any;
  readonly onInputChange?: (inputValue: string) => void;
  readonly inputValue?: string;
  readonly className?: string;
  readonly disableClear?: boolean;
  readonly loading?: boolean;
  readonly limitTags?: number;
  readonly onOpen?: (evt: SyntheticEvent) => void;
  readonly onClose?: (evt: SyntheticEvent) => void;
  readonly open?: boolean;
  readonly disableCloseOnSelect?: boolean;
  readonly disableOptionsLimit?: boolean;
  readonly FooterActions?: ReactNode;
  readonly PaperComponent?: (params: HTMLAttributes<HTMLElement>) => JSX.Element;
  readonly noOptionsText?: ReactNode;
  readonly startAdornment?: ReactNode;
};

export type SelectFieldProps<T> = SelectFieldNonFormProps & {
  readonly value?: T | T[] | null;
  readonly options?: T[];
  readonly onChange: (
    value: AutocompleteValue<T, SelectFieldProps<T>['isMultiple'], SelectFieldProps<T>['disableClear'], false>,
  ) => void;
  readonly getOptionLabel?: (value: T) => string;
  readonly getOptionKey?: (value: T) => string;
  readonly getOptionDisabled?: (value: T) => boolean;
  readonly isOptionEqualToValue?: (value: T, option: T) => boolean;
  readonly renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
  ) => ReactNode;
  readonly renderTags?: (value: T[], getTagProps: AutocompleteRenderGetTagProps) => ReactNode;
  readonly filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  enableVirtualKeyboard?: VirtualKeyboardPopperEnable;
};

const getAutocompleteStyles = (theme: Theme, width: number | string, readOnly?: boolean) => {
  const {
    palette: { getColor },
    spacing,
  } = theme;
  const baseStyles = {
    width,
    '& .MuiAutocomplete-popupIndicator svg': {
      backgroundColor: 'inherit',
    },
    '&&&& .MuiFormControlLabel-root .MuiFormControlLabel-label': {
      color: 'red',
    },
    '&&& .MuiAppChip': {
      ':not(.selected)': {
        backgroundColor: 'transparent',
        cursor: 'inherit',
        border: `1px solid ${getColor(theme.client.chip.baseChip.notSelected.readOnly.borderColor)}`,
      },
    },
  };
  if (readOnly) {
    return {
      ...baseStyles,
      '&& .MuiFormLabel-root': {
        paddingBottom: spacing(2),
      },
      '&& .MuiOutlinedInput-root': {
        paddingRight: spacing(2),
      },
      '&&& .MuiAppChip': {
        ...baseStyles['&&& .MuiAppChip'],
        margin: spacing(2, 2, 0, 0),
      },
    };
  }
  return baseStyles;
};

export function SelectField<T>({
  readOnly,
  value,
  placeholder,
  id,
  disabled,
  options,
  label,
  size,
  onChange,
  onScrollEnd,
  onInputChange,
  inputValue,
  helperText,
  errorMessage,
  type,
  error,
  className,
  isMultiple,
  getOptionLabel,
  getOptionDisabled,
  isOptionEqualToValue,
  renderTags,
  filterOptions,
  loading,
  limitTags = 1,
  width = '100%',
  disableClear = false,
  open: externalOpen,
  onOpen: externalOnOpen,
  onClose: externalOnClose,
  disableCloseOnSelect,
  renderOption,
  required,
  FooterActions,
  StartIcon = () => null,
  HelperTextIcon = () => null,
  startAdornment,
  noOptionsText,
  disableOptionsLimit = false,
  showErrorBorder = false,
  PaperComponent,
  getOptionKey,
  textAlign,
  enableVirtualKeyboard,
}: SelectFieldProps<T>) {
  const theme = useTheme();

  const isJumboSize = size === 'jumbo';
  const [internalOpen, setInternalOpen] = useState(externalOpen ?? false);

  const open = externalOpen ?? internalOpen;

  const onOpenInternal = useCallback(
    (evt: SyntheticEvent) => {
      setInternalOpen(true);
      externalOnOpen?.(evt);
    },
    [externalOnOpen],
  );

  const onCloseInternal = useCallback(
    (evt: SyntheticEvent) => {
      setInternalOpen(false);
      externalOnClose?.(evt);
    },
    [externalOnClose],
  );

  const [scroll, setScroll] = useState<Element | null>(null);
  const [scrollHeight, setScrollHeight] = useState(0);

  const sxData = useMemo(() => getAutocompleteStyles(theme, width, readOnly), [theme, width, readOnly]);

  const handleChange = (
    evt: SyntheticEvent,
    value: AutocompleteValue<T, typeof isMultiple, typeof disableClear, false>,
  ) => {
    if (!(disabled || readOnly)) {
      onChange && onChange(value);
    }
  };

  const internalRenderTags = useMemo(() => {
    if (renderTags) {
      return renderTags;
    }
    return (
      value: T[],
      getTagProps: (data: { index: number }) => TagProps,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ownerState: AutocompleteOwnerState<any, any, any, any>,
    ) =>
      value.map((option: T, index: number) => {
        const { onDelete, key, ...props } = getTagProps({ index });
        const label =
          ownerState.getOptionLabel?.(option) ??
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (option as any).label ??
          option;
        return (
          <Chip
            {...props}
            EndIcon={readOnly ? undefined : <FontAwesomeIcon icon={faXmark} />}
            key={key}
            label={label}
            selected={!readOnly}
            onClick={onDelete}
          />
        );
      });
  }, [readOnly, renderTags]);

  useEffect(() => {
    if (scroll !== null && onScrollEnd) {
      scroll.scrollTop = scrollHeight;
    }
  }, [scroll, scrollHeight, options, onScrollEnd]);

  const stopPropagation = (evt: SyntheticEvent) => {
    evt.stopPropagation();
    evt.preventDefault();
  };

  const PaperComponentInternal = useCallback(
    ({ children, ...props }: HTMLAttributes<HTMLElement>) => (
      <Paper {...props} onClick={stopPropagation} onMouseDown={stopPropagation}>
        {children}
        {FooterActions}
      </Paper>
      // we have to memory PaperComponent and not refresh if we want memory scroll works
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ),
    [],
  );

  const placeholderValue = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) && value.length === 0 ? placeholder : undefined;
    }
    return placeholder;
  }, [isMultiple, placeholder, value]);

  const onBlur = () => {
    const hasValue = Array.isArray(value) ? value.length !== 0 : !!value;

    if (inputValue?.length === 0 && !hasValue) {
      onChange(null);
    }
  };

  return (
    <Autocomplete<T, typeof isMultiple, typeof disableClear>
      autoHighlight={(inputValue?.length || 0) > 0}
      autoSelect={(inputValue?.length || 0) > 0}
      className={className}
      clearIcon={<ClearButton size={size} />}
      disableClearable={!!disableClear}
      disableCloseOnSelect={disableCloseOnSelect}
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionDisabled={getOptionDisabled}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      id={id}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      limitTags={readOnly ? undefined : limitTags}
      ListboxComponent={SelectFieldListbox}
      multiple={!!isMultiple}
      noOptionsText={noOptionsText}
      open={open}
      options={options || []}
      PaperComponent={FooterActions ? PaperComponentInternal : PaperComponent}
      popupIcon={<CUSTOM_ICONS.Expand />}
      readOnly={readOnly}
      renderOption={renderOption}
      renderTags={internalRenderTags}
      sx={sxData}
      value={value || null}
      ListboxProps={
        {
          isJumbo: isJumboSize,
          disableOptionsLimit,
          onScroll: (event: SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight) {
              setScroll(listboxNode);
              setScrollHeight(listboxNode.scrollTop - 20);
              onScrollEnd?.();
            }
          },
        } satisfies ComponentProps<typeof SelectFieldListbox> as HTMLAttributes<HTMLUListElement>
      }
      renderInput={(params) => (
        <VirtualKeyboardPopper
          enable={enableVirtualKeyboard && internalOpen ? enableVirtualKeyboard : 'off'}
          value={inputValue || ''}
          onChange={(e) => onInputChange?.(e.target.value)}
          onClose={onBlur}
        >
          {({ onOpen, onClose }) => (
            <StyledInternalGrid error={error} isJumbo={isJumboSize}>
              <InternalField
                {...params}
                error={error}
                errorMessage={errorMessage}
                helperText={helperText}
                HelperTextIcon={HelperTextIcon}
                label={label}
                placeholder={placeholderValue}
                readOnly={readOnly}
                required={required}
                showErrorBorder={showErrorBorder}
                size={size}
                textAlign={textAlign}
                type={type}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <StartIcon className="field-start-icon" />
                      {startAdornment}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={20} />}
                      {!loading && !readOnly && (
                        <DropdownIndicatorContainer position="end">
                          {params.InputProps.endAdornment}
                        </DropdownIndicatorContainer>
                      )}
                    </>
                  ),
                }}
                onBlur={onClose}
                onFocus={onOpen}
              />
            </StyledInternalGrid>
          )}
        </VirtualKeyboardPopper>
      )}
      slotProps={{
        popper: {
          placement: enableVirtualKeyboard && 'bottom-start',
        },
        paper: {
          className: enableVirtualKeyboard && 'max-h-36 overflow-auto',
        },
      }}
      onChange={handleChange}
      onClose={onCloseInternal}
      onInputChange={(_, value) => onInputChange?.(value)}
      onOpen={onOpenInternal}
    />
  );
}
